import React, { useState, useRef } from "react";
import {
  Stack,
  PrimaryButton,
  DefaultButton,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Pivot,
  PivotItem,
} from "@fluentui/react";
import SelectCobrandsForm from "../../Forms/SelectCobrandsForm";
import FeatureSummary from "../../Util/FeatureSummary";
import SubuserSelector, { SUBUSER_OPTIONS } from "./SubuserSelector";
import UnsubscribeTable from "./UnsubscribeTable";
import UnsubscribeSummary from "./UnsubscribeSummary";
import useUnsubscribedUsersReport from "./useUnsubscribedUsersReport";

const UnsubscribedUsersReport = () => {
  const selectCobrandsFormRef = useRef(null);
  const [selectedCobrands, setSelectedCobrands] = useState([]);
  const [selectionInfo, setSelectionInfo] = useState(null);
  const [selectedSubusers, setSelectedSubusers] = useState([SUBUSER_OPTIONS[0]]);

  const { reportData, loading, error, generateReport, exportToCsv } = useUnsubscribedUsersReport();

  const handleCobrandSelected = (cobrands, metadata) => {
    if (cobrands && cobrands.length > 0) {
      setSelectedCobrands(cobrands);
      setSelectionInfo(metadata);
    }
  };

  const handleFetchReport = () => generateReport(selectedCobrands, selectedSubusers);
  const handleExportToCSV = () => exportToCsv(selectionInfo);

  const canGenerateReport = selectedCobrands.length > 0 && selectedSubusers.length > 0 && !loading;

  return (
    <div style={{ padding: "20px" }}>
      <Stack tokens={{ childrenGap: 20 }} style={{ maxWidth: "800px", marginTop: "20px" }}>
        <SelectCobrandsForm ref={selectCobrandsFormRef} onCobrandsSelected={handleCobrandSelected} />

        {selectedCobrands.length > 0 && selectionInfo && (
          <MessageBar messageBarType={MessageBarType.success}>
            Selected {selectionInfo.type === 'client' ? 'Client' : selectionInfo.type === 'cobrandTag' ? 'Cobrand Tag' : 'Cobrand'}: <strong>{selectionInfo.name}</strong> ({selectedCobrands.length} cobrand{selectedCobrands.length !== 1 ? 's' : ''})
          </MessageBar>
        )}

        <SubuserSelector
          selectedSubusers={selectedSubusers}
          onSubusersChange={setSelectedSubusers}
        />

        <Stack horizontal tokens={{ childrenGap: 10 }}>
          <PrimaryButton
            text="Generate Report"
            onClick={handleFetchReport}
            disabled={!canGenerateReport}
            iconProps={{ iconName: "DocumentSearch" }}
          />
          {reportData && (
            <DefaultButton
              text="Export to CSV"
              onClick={handleExportToCSV}
              iconProps={{ iconName: "Download" }}
              disabled={
                !reportData.databaseUnsubscribes?.users?.length &&
                !reportData.sendGridUnsubscribes?.users?.length
              }
            />
          )}
        </Stack>
      </Stack>

      {loading && (
        <Stack horizontalAlign="center" style={{ marginTop: "40px" }}>
          <Spinner size={SpinnerSize.large} label="Loading unsubscribed users..." />
        </Stack>
      )}

      {error && (
        <MessageBar messageBarType={MessageBarType.error} style={{ marginTop: "20px" }}>
          Error loading report: {error.message}
        </MessageBar>
      )}

      {reportData && !loading && (
        <div style={{ marginTop: "40px" }}>
          <Pivot>
            <PivotItem
              headerText={`Database Unsubscribes (${reportData.databaseUnsubscribes?.totalCount || 0})`}
              itemIcon="Database"
            >
              <UnsubscribeTable
                users={reportData.databaseUnsubscribes?.users}
                infoMessage="These users opted out via the platform (dbo.EMLMemberUnsubscribe table)."
                emptyMessage="No database unsubscribes found for this cobrand."
              />
            </PivotItem>

            <PivotItem
              headerText={`SendGrid Unsubscribes (${reportData.sendGridUnsubscribes?.totalCount || 0})`}
              itemIcon="Send"
            >
              <UnsubscribeTable
                users={reportData.sendGridUnsubscribes?.users}
                infoMessage="These users unsubscribed from marketing emails via SendGrid."
                emptyMessage="No SendGrid unsubscribes found for this cobrand."
                subuserName={reportData.sendGridUnsubscribes?.subuserName}
              />
            </PivotItem>

            <PivotItem headerText="Summary" itemIcon="Chart">
              <UnsubscribeSummary
                databaseCount={reportData.databaseUnsubscribes?.totalCount}
                sendGridCount={reportData.sendGridUnsubscribes?.totalCount}
              />
            </PivotItem>
          </Pivot>
        </div>
      )}

      <FeatureSummary
        title="Unsubscribed Users Report"
        summary="View all users who have unsubscribed from emails for a specific cobrand, cobrand tag, or client. Data is aggregated from both the database (platform opt-outs) and SendGrid (marketing email unsubscribes). Allows for exporting files in cases where someone might want a report delivered. Unless you specifically need to check the QA data, you won't need to change the subuser option. The results will contain users from cobrands with account emails on so we can matchup SendGrid info to a user in the database."
      />
    </div>
  );
};

export default UnsubscribedUsersReport;
